import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import ReactMarkdown from 'react-markdown'
import { Loader2, AlertCircle, Clock, FileText, Users, Target, Wrench, Lightbulb, ArrowLeft, XCircle, Edit3, CheckCircle, CreditCard } from 'lucide-react'
import { Sidebar } from '@/shared/components'
import { PaymentButton } from '@/features/payment'
import { useProjectByShortId, useTeam } from '../hooks'

interface InfoSectionProps {
  icon: React.ReactNode
  label: string
  value?: string | null
}

function InfoSection({ icon, label, value }: InfoSectionProps) {
  if (!value) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground-muted">
        {icon}
        {label}
      </div>
      <div className="prose prose-sm max-w-none text-foreground prose-p:my-1 prose-p:leading-relaxed prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-strong:text-foreground">
        <ReactMarkdown>{value}</ReactMarkdown>
      </div>
    </div>
  )
}

export function ProjectPendingPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: project, isLoading, error } = useProjectByShortId(shortId)
  const { data: team } = useTeam(project?.team_id)

  // If project is no longer pending, redirect to kanban
  if (project && project.status !== 'pending') {
    navigate(`/p/${shortId}`, { replace: true })
    return null
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // CADRAGE-V2-001: Projects are now auto-validated, no manual review
  // Keep rejection logic for backward compatibility with existing rejected projects
  const isValidated = true // All new projects are auto-validated
  const isRejected = !!project?.rejection_reason
  const isUnderReview = false // No more manual review step

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-auto bg-background-secondary">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-8 py-6">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-background-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Title group */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {team?.name || ''}
              </span>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-foreground">
                  {project?.name || t('common.loading')}
                </h1>
                {isRejected ? (
                  <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                    {t('projectPending.revisionNeeded')}
                  </span>
                ) : isValidated ? (
                  <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                    {t('projectPending.approved')}
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                    {t('projectPending.underReview')}
                  </span>
                )}
              </div>
              {project && (
                <p className="text-xs text-foreground-muted">
                  {t('projectPending.submittedOn', { date: formatDate(project.created_at) })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
            </div>
          ) : error ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 text-destructive">
              <AlertCircle className="h-8 w-8" />
              <p>{t('common.error')}</p>
            </div>
          ) : project ? (
            <div className="mx-auto max-w-3xl space-y-8">
              {/* Status Card - Different for each state */}
              {isRejected ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                      <XCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-red-900">
                        {t('projectPending.rejectionTitle')}
                      </h2>
                      <p className="mt-1 text-red-800">
                        {t('projectPending.rejectionDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : isValidated ? (
                <div className="rounded-xl border border-green-200 bg-green-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-green-900">
                        {t('projectPending.validatedTitle')}
                      </h2>
                      <p className="mt-1 text-green-800">
                        {t('projectPending.validatedDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100">
                      <Clock className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-amber-900">
                        {t('projectPending.reviewTitle')}
                      </h2>
                      <p className="mt-1 text-amber-800">
                        {t('projectPending.reviewDescription')}
                      </p>
                      <p className="mt-3 text-sm text-amber-700">
                        {t('projectPending.reviewDelay')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Card - Only shown when validated */}
              {isValidated && (
                <div className="rounded-xl border-2 border-accent bg-accent/5 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10">
                      <CreditCard className="h-6 w-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-foreground">
                        {t('projectPending.paymentTitle')}
                      </h2>
                      <p className="mt-1 text-foreground-muted">
                        {t('projectPending.paymentDescription')}
                      </p>
                      <div className="mt-4">
                        <PaymentButton
                          teamId={project.team_id}
                          projectId={project.id}
                          paymentType="scoping"
                          size="lg"
                        />
                      </div>
                      <p className="mt-4 text-xs text-foreground-muted">
                        {t('projectPending.paymentSecure')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection Reason - Only shown when rejected */}
              {isRejected && (
                <div className="rounded-xl border border-border bg-background p-6">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">
                    {t('projectPending.rejectionReason')}
                  </h3>
                  <p className="whitespace-pre-wrap text-sm text-foreground-muted">
                    {project.rejection_reason}
                  </p>
                  <button
                    onClick={() => navigate(`/p/${shortId}/edit`)}
                    className="mt-6 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                  >
                    <Edit3 className="h-4 w-4" />
                    {t('projectPending.editAndResubmit')}
                  </button>
                </div>
              )}

              {/* Submitted Information */}
              <div className="rounded-xl border border-border bg-background p-6">
                <h3 className="mb-6 text-lg font-semibold text-foreground">
                  {t('projectPending.submittedInfo')}
                </h3>

                <div className="space-y-6">
                  <InfoSection
                    icon={<FileText className="h-4 w-4" />}
                    label={t('precadrage.form.description')}
                    value={project.description}
                  />

                  <InfoSection
                    icon={<Target className="h-4 w-4" />}
                    label={t('precadrage.form.objective')}
                    value={project.objective}
                  />

                  <InfoSection
                    icon={<Users className="h-4 w-4" />}
                    label={t('precadrage.form.targetUsers')}
                    value={project.target_users}
                  />

                  <InfoSection
                    icon={<Wrench className="h-4 w-4" />}
                    label={t('precadrage.form.existingAssets')}
                    value={project.existing_assets ? t(`precadrage.form.existingAssetsOptions.${project.existing_assets}`) : null}
                  />

                  {project.constraints && (
                    <InfoSection
                      icon={<AlertCircle className="h-4 w-4" />}
                      label={t('precadrage.form.constraints')}
                      value={project.constraints}
                    />
                  )}

                  {project.inspirations && (
                    <InfoSection
                      icon={<Lightbulb className="h-4 w-4" />}
                      label={t('precadrage.form.inspirations')}
                      value={project.inspirations}
                    />
                  )}
                </div>
              </div>

              {/* What's next - Only show for pending (not rejected, not validated) */}
              {isUnderReview && (
                <div className="rounded-xl border border-border bg-background p-6">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">
                    {t('projectPending.whatsNext')}
                  </h3>
                  <ol className="space-y-3 text-sm text-foreground-muted">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-white">1</span>
                      {t('projectPending.step1')}
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-border text-xs font-medium text-foreground-muted">2</span>
                      {t('projectPending.step2')}
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-border text-xs font-medium text-foreground-muted">3</span>
                      {t('projectPending.step3')}
                    </li>
                  </ol>
                </div>
              )}

              {/* What happens after payment - Only show when validated */}
              {isValidated && (
                <div className="rounded-xl border border-border bg-background p-6">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">
                    {t('projectPending.afterPaymentTitle')}
                  </h3>
                  <ol className="space-y-3 text-sm text-foreground-muted">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-medium text-white">1</span>
                      {t('projectPending.afterPaymentStep1')}
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-border text-xs font-medium text-foreground-muted">2</span>
                      {t('projectPending.afterPaymentStep2')}
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-border text-xs font-medium text-foreground-muted">3</span>
                      {t('projectPending.afterPaymentStep3')}
                    </li>
                  </ol>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  )
}
